import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';

import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  togglePinnedFiltersState,
  selectTradeTicketsPinnedFilters,
} from '@store';
import {
  TRADE_TICKETS_STAGES_NAMES,
  TRADE_TICKETS_CONTRACT_PERIODS,
} from 'components/tradeTickets/constants';
import { useHasFilters } from 'hooks/common';

export const useTradeTicketsFilterConfig = ({
  assignees,
  viewMode = 'list',
} = {}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const pinnedFilters = useSelector(selectTradeTicketsPinnedFilters);

  const stageOptions = useMemo(
    () =>
      Object.entries(TRADE_TICKETS_STAGES_NAMES).map(([key, val]) => ({
        key,
        text: val.label,
        value: val.value,
      })),
    []
  );

  const contractPeriodOptions = useMemo(
    () =>
      Object.entries(TRADE_TICKETS_CONTRACT_PERIODS).map(([key, val]) => ({
        key,
        text: val.label,
        value: key,
      })),
    []
  );

  const filterCategories = useMemo(
    () => [
      ...(viewMode === 'list'
        ? [
            {
              id: 'stage',
              label: 'Stage',
              icon: RadioButtonUncheckedIcon,
              options: stageOptions,
              multiSelect: true,
            },
          ]
        : []),
      {
        id: 'contractPeriod',
        label: 'Contract Period',
        icon: RadioButtonUncheckedIcon,
        options: contractPeriodOptions,
        multiSelect: true,
      },
      {
        id: 'assignee',
        label: 'Assignee',
        icon: RadioButtonUncheckedIcon,
        options: assignees || [],
        multiSelect: true,
      },
      {
        id: 'createdAt',
        label: 'Created At',
        icon: RadioButtonUncheckedIcon,
        type: 'dateTimeRange',
        dateOnly: true,
      },
    ],
    [stageOptions, contractPeriodOptions, assignees, viewMode]
  );

  const handlePinnedFilterSelect = (pinnedFilter) => {
    dispatch(
      togglePinnedFiltersState({
        board: 'tradeTickets',
        pinnedFilter,
      })
    );
  };

  const { hasFilters } = useHasFilters({
    pinnedFilters,
    omitParams: ['page', 'view', 'pageSize', 'order', 'orderBy', 'query'],
  });

  const currentFilters = useMemo(
    () => ({
      stage: router.query.stage
        ? router.query.stage.split(',').filter(Boolean)
        : [],
      contractPeriod: router.query.contractPeriod
        ? router.query.contractPeriod.split(',').filter(Boolean)
        : [],
      assignee: router.query.assignee
        ? router.query.assignee.split(',').filter(Boolean)
        : [],
      createdAt: {
        dateFrom: router.query.dateFrom || null,
        dateTo: router.query.dateTo || null,
      },
    }),
    [router.query]
  );

  return {
    filterCategories,
    currentFilters,
    handlePinnedFilterSelect,
    pinnedFilters,
    supportPinnedFilters: true,
    hasFilters,
  };
};
